/**
 * Extract the most specific, human-readable error message from any thrown value.
 *
 * Precedence: combined field-level validationErrors -> backend `message` ->
 * backend `error` code -> the error's own `message` -> caller fallback.
 * Always returns a non-empty string so callers never show an empty toast.
 */
export const getErrorMessage = (error: unknown, fallback?: string): string => {
  const last = fallback && fallback.trim() ? fallback : 'An error occurred';

  if (!error || typeof error !== 'object') {
    return last;
  }

  const response = (error as { response?: { data?: unknown } }).response;
  const data = response?.data;

  if (data && typeof data === 'object') {
    const { validationErrors, message, error: errorCode } = data as {
      validationErrors?: Record<string, string>;
      message?: string;
      error?: string;
    };

    if (validationErrors && typeof validationErrors === 'object') {
      const parts = Object.values(validationErrors).filter(
        (v): v is string => typeof v === 'string' && v.trim().length > 0
      );
      if (parts.length > 0) {
        return parts.join('; ');
      }
    }

    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }

    if (typeof errorCode === 'string' && errorCode.trim().length > 0) {
      return errorCode;
    }
  }

  const ownMessage = (error as { message?: unknown }).message;
  if (typeof ownMessage === 'string' && ownMessage.trim().length > 0) {
    return ownMessage;
  }

  return last;
};
